import { DiaryEntry } from './diary/types';
import { updateEntry as updateStorageEntry, uploadEntryImage } from './diary/storage';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { t } from "@/utils/translations";
import { Language } from "@/contexts/language";
import type { GalleryPhoto } from "@/components/gallery/carousel/types";

export const updateEntry = async (updatedEntry: DiaryEntry): Promise<void> => {
  // Call the updateEntry function from storage.ts
  await updateStorageEntry(updatedEntry);
  
  // Make sure any images in the entry are also in the gallery
  if (updatedEntry.images && updatedEntry.images.length > 0 && updatedEntry.userId) {
    try {
      // For each image in the entry, make sure it's in the gallery
      for (const imageUrl of updatedEntry.images) {
        await ensureImageInGallery(imageUrl, updatedEntry.userId);
      }
    } catch (error) {
      console.error("Non-critical error ensuring images in gallery:", error);
      // Don't fail the update if this fails
    }
  }
};

export const uploadImage = async (file: File, userId: string, language?: Language): Promise<string | null> => {
  try {
    if (!file) {
      toast.error("No file selected");
      return null;
    }

    if (!userId) {
      toast.error("User ID is required for uploading images");
      return null;
    }

    console.log(`Starting image upload for user ${userId} with file ${file.name}`);

    // Validate the file before uploading
    if (!file.type.startsWith('image/')) {
      toast.error("Only image files are allowed");
      return null;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("Image size should be less than 5MB");
      return null;
    }

    // Call the uploadEntryImage function from storage.ts
    const imageUrl = await uploadEntryImage(file, userId);
    
    if (!imageUrl) {
      // The uploadEntryImage function already handles error toasts
      console.error("Failed to get image URL after upload");
      return null;
    }
    
    console.log("Image upload successful, URL:", imageUrl);
    
    // Save the image to the gallery
    await saveImageToGallery(imageUrl, userId, language);
    
    return imageUrl;
  } catch (error) {
    console.error("Error uploading image:", error);
    toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  }
};

// Save an image to the gallery
export const saveImageToGallery = async (imageUrl: string, userId: string, language?: Language): Promise<void> => {
  try {
    if (!imageUrl || !userId) {
      console.error("Missing image URL or user ID");
      return;
    }
    
    console.log("Saving image to gallery:", imageUrl);
    
    // First check if the image already exists to prevent duplicates
    const { data: existingPhoto, error: checkError } = await supabase
      .from('gallery_photos')
      .select('id')
      .eq('url', imageUrl)
      .eq('user_id', userId);
      
    if (checkError) {
      console.error("Error checking for existing image:", checkError);
      return;
    }
    
    // If the image already exists, don't add it again
    if (existingPhoto && existingPhoto.length > 0) {
      console.log("Image already exists in gallery, skipping insertion:", existingPhoto);
      return;
    }
    
    const { data, error } = await supabase
      .from('gallery_photos')
      .insert({
        title: language ? t('gallery.photoFromDiary', language) : 'Photo from diary entry',
        url: imageUrl,
        user_id: userId,
        category: 'recent'
      })
      .select();

    if (error) {
      console.error("Error saving image to gallery:", error);
    } else {
      console.log("Image successfully saved to gallery:", data);
    }
  } catch (err) {
    console.error("Error in saveImageToGallery:", err);
  }
};

// Check if an image is already in the gallery, add it if not
export const ensureImageInGallery = async (imageUrl: string, userId: string): Promise<void> => {
  try {
    // First check if the image is already in the gallery - use select() instead of maybeSingle()
    const { data, error } = await supabase
      .from('gallery_photos')
      .select('id')
      .eq('url', imageUrl)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error checking gallery for image:", error);
      return;
    }
    
    // If the image isn't in the gallery yet, add it
    if (!data || data.length === 0) {
      console.log("Image not found in gallery, adding:", imageUrl);
      await saveImageToGallery(imageUrl, userId);
    } else {
      console.log("Image already exists in gallery, no need to add again:", data);
    }
  } catch (err) {
    console.error("Error in ensureImageInGallery:", err);
  }
};
